import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

/**
 * GeoContext — single source of truth for country / currency across the app.
 *
 * Why it exists:
 *  - Indian users were seeing USD because `/api/geo` was being called only
 *    when the PricingModal opened, AND the Emergent preview edge doesn't
 *    inject `cf-ipcountry`. We now:
 *      1. Fetch on App boot so the currency is known before ANY pricing
 *         surface renders.
 *      2. Persist the last-known `{country, currency}` in localStorage so
 *         repeat visitors skip the 2.5s ipapi round-trip.
 *      3. Expose `setOverride(country)` so users (or our "Pay in ₹ / $"
 *         toggle) can manually flip the currency — critical for NRIs on
 *         foreign IPs who still want INR or vice-versa.
 */
const Ctx = createContext(null);
const LS_KEY = "oak_geo_v1";

function readCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.country) return null;
    // 24h TTL so a user that moves across borders isn't pinned forever.
    if (Date.now() - (obj.t || 0) > 24 * 3600 * 1000) return null;
    return obj;
  } catch { return null; }
}

function writeCache(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ ...data, t: Date.now() })); } catch {}
}

function clearCache() {
  try { localStorage.removeItem(LS_KEY); } catch {}
}

export function GeoProvider({ children }) {
  const cached = readCache();
  const [geo, setGeo] = useState(
    cached || { country: null, is_india: false, currency: "USD", source: null, loading: true },
  );
  const [override, setOverrideCountry] = useState(() => {
    try { return localStorage.getItem("oak_geo_override") || null; } catch { return null; }
  });

  const applyGeo = useCallback((next, isOverride = false) => {
    const safe = {
      country: next?.country || null,
      is_india: Boolean(next?.is_india),
      currency: next?.currency || "USD",
      source: isOverride ? "user_override" : (next?.source || null),
      loading: false,
    };
    setGeo(safe);
    if (!isOverride) writeCache(safe);
  }, []);

  const fetchFresh = useCallback(async (forceCountry = null) => {
    try {
      const url = forceCountry ? `/geo?country=${encodeURIComponent(forceCountry)}` : "/geo";
      const { data } = await api.get(url);
      applyGeo(data, Boolean(forceCountry));
      return data;
    } catch {
      // Keep cached/default on failure — never block the UI.
      setGeo((g) => ({ ...g, loading: false }));
      return null;
    }
  }, [applyGeo]);

  // Eager boot — fetch on mount unless cache already satisfies us.
  useEffect(() => {
    // Honour a persisted manual override above the cache.
    if (override) {
      fetchFresh(override);
      return;
    }
    if (cached) {
      // Refresh in the background so a stale cache self-heals.
      fetchFresh();
      return;
    }
    fetchFresh();
     
  }, []);

  const setOverride = useCallback((country) => {
    if (!country) {
      try { localStorage.removeItem("oak_geo_override"); } catch {}
      setOverrideCountry(null);
      clearCache();
      fetchFresh();
      return;
    }
    const code = country.toUpperCase();
    try { localStorage.setItem("oak_geo_override", code); } catch {}
    setOverrideCountry(code);
    fetchFresh(code);
  }, [fetchFresh]);

  const value = useMemo(() => ({
    ...geo,
    override,
    refresh: () => fetchFresh(override || null),
    setOverride,
  }), [geo, override, fetchFresh, setOverride]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGeo() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useGeo must be used inside <GeoProvider/>");
  return v;
}
